import React, { useMemo, useState } from 'react';
import {
  Drawer,
  Toolbar,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Box,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

type Submenu = {
  label: string;
  path: string;
};

type MenuItemConfig = {
  id: string;
  label: string;
  path?: string; // optional — parent may be non-clickable
  children?: Submenu[];
};

const drawerWidth = 240;

/**
 * Example menu configuration.
 * Replace / edit these items as per your requirements.
 */
const defaultMenuConfig: MenuItemConfig[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/admin/dashboard',
  },
  {
    id: 'users',
    label: 'Staff Management',
    children: [
      { label: 'Users', path: '/admin/users' },
      { label: 'Roles', path: '/admin/users/roles' },
    ],
  },
  {
    id: 'auctions',
    label: 'Auctions',
    children: [
      { label: 'Auctions', path: '/admin/auctions' },
      { label: 'Sellers', path: '/admin/sellers' },
    ],
  },
  {
    id: 'buyers',
    label: 'Buyers',
    path: '/admin/buyers',
  },
  {
    id: 'catalog',
    label: 'Catalog',
    children: [
      { label: 'Items', path: '/admin/catalog/items' },
      { label: 'Subitems', path: '/admin/catalog/sub-items' },
    ],
  },
];

interface AdminSideNavProps {
  open: boolean;
  menuConfig?: MenuItemConfig[];
}

const AdminSideNav: React.FC<AdminSideNavProps> = ({ open, menuConfig = defaultMenuConfig }) => {
  const nav = useNavigate();
  const location = useLocation();

  // Track which menu ids are expanded
  // By default expand those items that contain the current path
  const initialExpanded = useMemo(() => {
    const expanded: Record<string, boolean> = {};
    menuConfig.forEach((m) => {
      if (m.children) {
        expanded[m.id] = m.children.some((c) => location.pathname.startsWith(c.path));
      } else {
        expanded[m.id] = false;
      }
    });
    return expanded;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuConfig]);

  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>(initialExpanded);

  const toggleExpand = (id: string) => {
    setExpandedMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleNavigate = (path?: string) => {
    if (!path) return;
    nav(path);
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    // exact or startsWith depending on your routing; using startsWith so nested routes still highlight
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <Drawer
      variant="persistent"
      open={open}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
    >
      <Toolbar sx={{ px: 2 }}>
        <Box>
          <Typography variant="h6" noWrap>
            Admin
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Control panel
          </Typography>
        </Box>
      </Toolbar>

      <List sx={{ px: 1 }}>
        {menuConfig.map((m) => {
          const hasChildren = !!m.children && m.children.length > 0;
          const isExpanded = !!expandedMap[m.id];
          const parentActive =
            isActive(m.path) || (hasChildren && (m.children ?? []).some((c) => isActive(c.path)));

          return (
            <Box key={m.id} sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => {
                  if (hasChildren) toggleExpand(m.id);
                  else handleNavigate(m.path);
                }}
                selected={parentActive && !hasChildren ? true : false}
                sx={{
                  pl: 2,
                  pr: 2,
                  bgcolor: parentActive && !hasChildren ? 'action.selected' : 'inherit',
                }}
              >
                <ListItemText
                  primary={m.label}
                  primaryTypographyProps={{ fontWeight: parentActive ? 'bold' : 'normal' }}
                />
                {hasChildren ? (
                  // simple arrows instead of icons
                  <Box component="span" sx={{ fontSize: 18 }}>
                    {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                  </Box>
                ) : null}
              </ListItemButton>

              {hasChildren && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {(m.children ?? []).map((c) => {
                      const active = isActive(c.path);
                      return (
                        <ListItemButton
                          key={c.path}
                          sx={{ pl: 4 }}
                          onClick={() => handleNavigate(c.path)}
                          selected={active}
                        >
                          <ListItemText
                            primary={c.label}
                            primaryTypographyProps={{
                              fontSize: 14,
                              fontWeight: active ? 'bold' : 'normal',
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </Box>
          );
        })}
      </List>
    </Drawer>
  );
};

export default AdminSideNav;
